/**
 * Whisper tiny ASR via @xenova/transformers loaded as ESM from CDN (not webpack).
 * Model downloads on first use (~39MB) and is cached by the browser.
 */

const ESM_URL = 'https://cdn.jsdelivr.net/npm/@xenova/transformers@2.17.2';
const MODEL_ID = 'Xenova/whisper-tiny.en';
const READY_EVENT = 'l123-transformers-ready';
const GLOBAL_KEY = '__L123_TRANSFORMERS__';

let scriptPromise: Promise<any> | null = null;
let transcriberPromise: Promise<any> | null = null;
let transcriber: any = null;

function getWin(): any {
  const g: any = typeof global !== 'undefined' ? global : {};
  return g.window || g;
}

/**
 * Inject a type=module script that dynamic-imports transformers and exposes it on window.
 * Avoids CRA/webpack trying to bundle the library.
 */
function loadTransformersEsm(): Promise<any> {
  const win = getWin();
  if (win[GLOBAL_KEY] && win[GLOBAL_KEY].pipeline) {
    return Promise.resolve(win[GLOBAL_KEY]);
  }
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const doc = win.document;
    const onReady = (ev: any) => {
      win.removeEventListener(READY_EVENT, onReady);
      const detail = ev && ev.detail;
      if (detail && detail.ok && win[GLOBAL_KEY]) {
        resolve(win[GLOBAL_KEY]);
      } else {
        scriptPromise = null;
        reject(new Error((detail && detail.err) || 'Không tải được Transformers từ CDN. Kiểm tra mạng rồi thử lại.'));
      }
    };
    win.addEventListener(READY_EVENT, onReady);

    const existing = doc.getElementById('l123-transformers-loader');
    if (existing) {
      // wait for event only
      setTimeout(() => {
        if (win[GLOBAL_KEY]) {
          win.removeEventListener(READY_EVENT, onReady);
          resolve(win[GLOBAL_KEY]);
        }
      }, 0);
      return;
    }

    const s = doc.createElement('script');
    s.id = 'l123-transformers-loader';
    s.type = 'module';
    // Inline module — string concat so CRA babel does not rewrite import()
    s.textContent =
      "import('" +
      ESM_URL +
      "').then(function(T){" +
      "window." +
      GLOBAL_KEY +
      "=T;" +
      "window.dispatchEvent(new CustomEvent('" +
      READY_EVENT +
      "',{detail:{ok:true}}));" +
      "}).catch(function(e){" +
      "window.dispatchEvent(new CustomEvent('" +
      READY_EVENT +
      "',{detail:{ok:false,err:String(e&&e.message||e)}}));" +
      "});";
    doc.head.appendChild(s);

    setTimeout(() => {
      if (!win[GLOBAL_KEY]) {
        win.removeEventListener(READY_EVENT, onReady);
        scriptPromise = null;
        reject(new Error('Hết thời gian tải Transformers. Kiểm tra mạng rồi thử lại.'));
      }
    }, 120000);
  });

  return scriptPromise;
}

export type LoadProgress = {
  status: string;
  progress?: number;
  file?: string;
};

/**
 * Load (or reuse) the Whisper tiny.en ASR pipeline.
 * onProgress receives download progress 0–1 when available.
 */
export async function ensureTranscriber(
  onProgress?: (info: LoadProgress) => void
): Promise<any> {
  if (transcriber) return transcriber;
  if (transcriberPromise) return transcriberPromise;

  transcriberPromise = (async () => {
    onProgress && onProgress({ status: 'Đang tải thư viện…' });
    const T = await loadTransformersEsm();
    if (!T || !T.pipeline) {
      throw new Error('Thư viện Transformers không khởi tạo được.');
    }
    if (T.env) {
      T.env.allowLocalModels = false;
    }
    onProgress && onProgress({ status: 'Đang tải model Whisper tiny (~39MB, lần đầu)…', progress: 0 });

    const pipe = await T.pipeline('automatic-speech-recognition', MODEL_ID, {
      quantized: true,
      progress_callback: (data: any) => {
        if (!onProgress) return;
        const status = (data && data.status) || '';
        let progress: number | undefined;
        if (typeof data.progress === 'number') {
          // xenova sometimes reports 0–100, sometimes 0–1
          const raw = data.progress > 1 ? data.progress / 100 : data.progress;
          progress = Math.max(0, Math.min(1, raw));
        } else if (data.loaded != null && data.total) {
          progress = Math.max(0, Math.min(1, data.loaded / data.total));
        }
        onProgress({
          status: status === 'done' || status === 'ready' ? 'Model sẵn sàng.' : `Đang tải model… ${status}`,
          progress,
          file: data && data.file,
        });
      },
    });
    transcriber = pipe;
    onProgress && onProgress({ status: 'Model sẵn sàng.', progress: 1 });
    return pipe;
  })();

  try {
    return await transcriberPromise;
  } catch (e) {
    transcriberPromise = null;
    throw e;
  }
}

/**
 * Transcribe 16 kHz mono Float32 PCM → English text.
 */
export async function transcribe(
  pcm16k: Float32Array,
  onProgress?: (info: LoadProgress) => void
): Promise<string> {
  if (!pcm16k || !pcm16k.length) {
    throw new Error('Không có âm thanh để nhận diện. Hãy thu lại.');
  }
  const pipe = await ensureTranscriber(onProgress);
  onProgress && onProgress({ status: 'Đang nhận diện giọng nói…' });

  // WhisperFeatureExtractor expects a Float32Array (16 kHz mono), not a wrapper object.
  const samples =
    pcm16k instanceof Float32Array ? pcm16k : new Float32Array(pcm16k as any);

  const result = await pipe(samples, {
    chunk_length_s: 15,
    stride_length_s: 0,
    return_timestamps: false,
  });

  const text = result && (result.text || (typeof result === 'string' ? result : ''));
  return String(text || '').trim();
}

export function isModelReady(): boolean {
  return !!transcriber;
}
